import { Button } from "@mantine/core";
import useInsightHook from "@/hooks/useInsightHook";
import AppLifecycleContext from "@/lib/contexts/AppLifecycleContext";
import { InsightContext } from "@/lib/contexts/InsightContext";
import { InsightsService } from "@/lib/services/InsightsService";
import { TOAST_MESSAGES } from "@/lib/utils/Constants";
import { ThirdPartyTracking } from "@/lib/utils/ThirdPartyTracking";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ConditionItem } from "types/types.d";
import { LoadingComponent } from "@/ui/components/Common/Loading/Loading";
import { EventsSetup } from "../Components/Setup/EventsSetup";
import { NameSetup } from "../Components/Setup/NameSetup";
import { SidePanel } from "@/ui/components/Common/SidePanel/SidePanel";
import "./insights_setup.scss";
import useConditionsValidityHook from "@/hooks/useConditionsValidityHook";
import { StringParam, useQueryParam } from "use-query-params";

interface IFeatureAdoptionSetupModal {
  isModalOpen: boolean;
  setIsModalOpen: (e: boolean) => void;
  insightId?: string;
}

/**
 * @deprecated not in use anymore
 */
const FeatureAdoptionSetupModal = ({
  isModalOpen,
  setIsModalOpen,
  insightId
}: IFeatureAdoptionSetupModal) => {
  // States that are used.
  const [isLoading, setIsLoading] = useState("idle");
  const [templateId, setTemplateId] = useState("");
  const [name, setName] = useState("");
  const [conditions, setConditions] = useState<Array<ConditionItem>>([]);

  const [isStoreLoader, setIsStoreLoader] = useState("idle");
  const navigate = useNavigate();
  const { stepsValidation } = useConditionsValidityHook();
  // We are passing attributes and events to the child components.
  const { insightEvents, insightAttributes } = useInsightHook();
  const { activeWorkspace } = useContext(AppLifecycleContext);

  // NOTE: We are going to provide cohort filter in insights as discussed internally.

  // const [audienceConditionType, setAudienceConditionType] = useState<
  //   "or" | "and"
  // >("or");
  // const [audienceConditions, setAudienceConditions] = useState<
  //   Array<ConditionItem>
  // >([]);

  // all service calls
  const insightService = new InsightsService();
  const createInsight = async () => {
    setIsStoreLoader("loading");

    await insightService
      .create(activeWorkspace.id, {
        name: name,
        conditions: {
          feature_events_conditions: conditions
        },
        type: "feature_adoption"
      })
      .then((res) => {
        if (res.data) {
          toast.success(TOAST_MESSAGES.INSIGHT_CREATED);
          const TPService = new ThirdPartyTracking();
          TPService.track("feature_adoption_insight_created");
          navigate({ search :`insightId=${res.data.id}`});
          setIsModalOpen(false);
        }
      })
      .catch((err) => {});
    setIsStoreLoader("loaded");
  };

  const updateInsight = async () => {
    setIsStoreLoader("loading");

    await insightService
      .update(activeWorkspace.id, templateId, {
        name: name,
        conditions: {
          feature_events_conditions: conditions
        }
      })
      .then((res) => {
        toast.success(TOAST_MESSAGES.INSIGHT_UPDATED);
        setIsModalOpen(false);
      })
      .catch((err) => {});
    setIsStoreLoader("loaded");
  };

  const getInsight = async () => {
    setIsLoading("loading");
    await insightService
      .get(activeWorkspace.id, insightId)
      .then((res) => {
        if (res.data) {
          setTemplateId(res.data.id);
          setName(res.data.name);
          // document.title = `${res.data.name} | Insight | Usermaven`;
          setConditions(res.data.conditions.feature_events_conditions);
        }
      })
      .catch((err) => {});
    setIsLoading("loaded");
  };

  useEffect(() => {
    if (insightId) {
      getInsight();
    } else {
      setIsLoading("loaded");
    }
  }, [insightId]);

  if (isLoading !== "loaded") {
    return (
      <>
        <LoadingComponent></LoadingComponent>
      </>
    );
  }

  const handleSubmission = async () => {
    if (name.length === 0) {
      toast.error("Please enter a name for your insight.");
      return;
    }
    const featureAdoptionConditionsCheck = stepsValidation(conditions, 1, 10);
    if (!featureAdoptionConditionsCheck) {
      return;
    }

    if (insightId) {
      await updateInsight();
    } else {
      await createInsight();
    }
  };

  return (
    <SidePanel opened={isModalOpen} onCancel={() => setIsModalOpen(false)} loading={true}>
      <h2 className="font-semibold mb-4">Feature Adoption</h2>

      <NameSetup
        name={name}
        setName={setName}
        placeholder={"e.g Users who have published content..."}
      />
      <InsightContext.Provider
        value={{
          insightEvents,
          insightAttributes
        }}>
        <EventsSetup
          conditions={conditions}
          setConditions={setConditions}
          title={"Select events for your feature"}
          description={"You can select more than one event if you have multiple actions."}
          conditionType={"or"}
        />
      </InsightContext.Provider>
      <div className="um-insights-setup__actions">
        <Button
          onClick={() => {
            setIsModalOpen(false);
          }}
          color="red">
          Cancel
        </Button>
        {templateId ? (
          <>
            <Button
              onClick={() => {
                handleSubmission();
              }}
              loading={isStoreLoader === "loading"}
              disabled={conditions.length === 0 || isStoreLoader === "loading"}>
              Update Insight
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                handleSubmission();
              }}
              loading={isStoreLoader === "loading"}
              disabled={conditions.length === 0 || isStoreLoader === "loading"}>
              Save Insight
            </Button>
          </>
        )}
      </div>
    </SidePanel>
  );
};

export default FeatureAdoptionSetupModal;
